// The week a league screen shows (APP_SHELL.md "Period stepper", scenario D4).
//
// THE ADDRESS HOLDS IT: `?period=5`. So the browser's back button steps back
// through the weeks you looked at, a link to a past week opens on it, and a
// reload keeps it (B-216). No `?period` means now, which is Home's week — the
// one containing today, moved onto a week the league plays (`periodNow`).
//
// Stepping backward shows past results; stepping forward shows what is
// coming up.

import { valueOf, type PeriodRef } from '@fsp/domain';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useHome, useSchedule } from './hooks';

export interface ViewedPeriod {
  /** The week the address asks for; undefined means now. */
  period: number | undefined;
  /** Now's week, from Home; null while it is read. */
  now: number | null;
  /** Every week the league plays, in order; empty while the schedule is read. */
  weeks: readonly PeriodRef[];
  /** The week on screen: the one asked for, or now. */
  shown: number | null;
  /** Show this week; null (or now's own) drops the address back to now. */
  go: (period: number | null) => void;
}

/** `?period=` as a week number, or undefined for anything that is not one. */
export function periodParam(raw: string | null): number | undefined {
  if (raw === null || !/^\d+$/.test(raw)) return undefined;
  const n = Number(raw);
  return n >= 1 ? n : undefined;
}

export function useViewedPeriod(): ViewedPeriod {
  const [params, setParams] = useSearchParams();
  const home = valueOf(useHome());
  const schedule = valueOf(useSchedule());
  const now = home?.period.index ?? null;
  const weeks = schedule?.periods.map((p) => p.period) ?? [];
  const asked = periodParam(params.get('period'));
  // A week the league does not play is not one to show: an old link, or a
  // number typed into the address. Before the schedule is read nothing is
  // known either way, so the asked week stands.
  const period =
    asked === undefined ||
    asked === now ||
    (weeks.length > 0 && !weeks.some((w) => w.index === asked))
      ? undefined
      : asked;
  const go = useCallback(
    (next: number | null) => {
      const out = new URLSearchParams(params);
      if (next === null || next === now) out.delete('period');
      else out.set('period', String(next));
      setParams(out);
    },
    [params, setParams, now],
  );
  return { period, now, weeks, shown: period ?? now, go };
}
